/* Next */
import Link from 'next/link'

/* Redux */
import Redux from '../../Redux'
import { useSelector } from 'src/redux/hooks'

/* Material */
import Typo from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Button from '@mui/material/Button'
import useStyles from './styles'

/* Material-Icons */
//import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import ClearIcon from '@mui/icons-material/Clear'
import InfoIcon from '@mui/icons-material/Info'

/* Functions */
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getProductUrl from 'src/functions/productUrl'
import renderDeliverytime from 'src/functions/renderDeliverytime'
import { isQuantityDiscountAvailable } from 'src/shared/discountShop'

/* Components */
import PriceDisplay from 'src/components/PriceDisplay'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import QuantityDiscountInfo from 'src/components/QuantityDiscountInfo'
import StockDiscountPriceBreakdown, {
  hasStockDiscountPriceBreakdown,
} from 'src/components/StockDiscountPriceBreakdown'

/* Next-Dynamic */
import dynamic from 'next/dynamic'
const CountdownClock = dynamic(() => import('src/components/CountdownClock/TextClock'), {
  ssr: false,
})

const WarenkorbInhalt = () => {
  const classes = useStyles()
  const touchDevice = useSelector(store => store.App.isTouchDevice)

  const { items, changeQty, deleteItem } = Redux()

  return <div>
    {Object.values(items).map(({
      id, key, name, image, attributesText, path, attributes,
      price, qty, deliverytime, stockDeliverytime,
      stock=0, onlyStock, purchaseMode, inquireOnly,
    },i) => {

      const options = attributes ? Object.values(attributes) : []
      const productUrl = getProductUrl(id, path, options)
      const imageUrl = image ? getImageUrl(image,id,'xs') : null

      const isInquiryOnly = inquireOnly || purchaseMode === 'inquiry'
      const safeDeliverytime = deliverytime || 0
      const showStockDiscountBreakdown = hasStockDiscountPriceBreakdown(price, qty)
      const showQuantityDiscount = typeof price !== 'number'
        && price.showPercent !== false
        && isQuantityDiscountAvailable(price.quantityDiscount, {
          onlyStock,
          stock,
        })
      const maxQty = isInquiryOnly
        ? qty
        : onlyStock
          ? stock
          : Infinity

      if (qty > maxQty) changeQty(key, maxQty)

      return <Card key={i+'-c'+key} className={[
        classes.card,
        !touchDevice ? 'hover' : null
      ].join(' ')}>

        <CardContent className={classes.content}>

          <div className={classes.gridRoot}>

            <Link href={productUrl} passHref legacyBehavior>
              <Card className={classes.image} role='button'>
                {imageUrl && <>
                  <img
                    alt={name}
                    src={imageUrl}
                    width={100} height={100}
                    loading='eager'
                  />
                  <AiGeneratedBadge aiGenerated={image?.aiGenerated} />
                </>}
              </Card>
            </Link>

            <div className={classes.gridInfos}>

              <div className={classes.titel}>
                <Link href={productUrl} passHref legacyBehavior>
                  <Typo component='a' fontWeight='bold'>
                    {name}
                  </Typo>
                </Link>
              </div>

              {attributesText && 
                <div className={classes.description}>
                  <List>
                    {attributesText.map((attribute,i)=>
                      <ListItem key={i} component='li'>
                        {attribute.titel}: {attribute.option}
                      </ListItem>
                    )}
                  </List>
                </div>
              }

              <div className={classes.price}>
                <PriceDisplay 
                  {...{price, qty}}
                  component='p' variant='body1' align='right' fontWeight='bold'
                  discountBadgeVariant='inline'
                  onlyStock={onlyStock}
                  stock={stock}
                />
                { typeof price !== 'number' && price.type === 'countdown' && price.dateEnd &&
                                    <CountdownClock 
                                      dateEnd={price.dateEnd}
                                      displayVariant={price.countdownVariant ?? undefined}
                                      className={classes.countdown}
                                      component='span' variant='body1' align='right' fontWeight='bold'
                                      size='small'
                                    />
                }
                {showQuantityDiscount && typeof price !== 'number' && (
                  <QuantityDiscountInfo
                    quantityDiscount={price.quantityDiscount}
                    dateEnd={price.dateEnd}
                    qty={qty}
                    className={classes.quantityDiscountInfo}
                    component='div'
                    variant='body2'
                  />
                )}
              </div>

              {isInquiryOnly &&
                <div className={classes.deliverytime}>
                  <Typo color='info.main'>Nur auf Anfrage erhältlich</Typo>
                </div>
              }

              {!showStockDiscountBreakdown && !isInquiryOnly && (stock > 0 || safeDeliverytime > 0) &&
                <div className={classes.deliverytime}>
                  {renderDeliverytime(safeDeliverytime, qty, stock, stockDeliverytime)}
                </div>
              }

              <div className={classes.qty}>
                <Typo>Menge: {qty}</Typo>
                {!isInquiryOnly && qty>=maxQty &&
                  <Typo color='error'>
                    <InfoIcon /> Auslaufartikel - Nur noch {maxQty} mal verfügbar
                  </Typo>
                }
                {isInquiryOnly &&
                  <Typo color='info.main'>
                    <InfoIcon /> Dieser Artikel kann nicht direkt online bestellt werden.
                  </Typo>
                }
              </div>

              {qty > 1 &&
                <div className={classes.singlePrice}>
                  <Typo>Einzelpreis: </Typo>
                  {showStockDiscountBreakdown
                    ? (
                      <StockDiscountPriceBreakdown
                        price={price}
                        qty={qty}
                        deliverytime={safeDeliverytime}
                        stockDeliverytime={stockDeliverytime}
                        showDeliverytime
                        showBackorderNotice
                        className={classes.singlePriceDiscountInfo}
                      />
                    )
                    : (
                      <PriceDisplay
                        {...{price}}
                        component='span' variant='body1'
                        discountBadgeVariant='inline'
                        onlyStock={onlyStock}
                        stock={stock}
                      />
                    )
                  }
                </div>
              }

              {/* +++++++++++++++++++++++++++++++++++++++++++++++++++ */}

              <div className={classes.buttons}>

                <div>
                  <Button 
                    title="Weniger"
                    size='small' color='inherit' variant='outlined'
                    onClick={()=>{changeQty(key,qty-1)}}
                    disabled={qty<2 || isInquiryOnly}
                  >
                    <RemoveIcon />
                  </Button>
                  <Button 
                    title="Mehr"
                    size='small' color='inherit' variant='outlined'
                    onClick={()=>{changeQty(key,qty+1)}}
                    disabled={qty>=maxQty || isInquiryOnly}
                  >
                    <AddIcon />
                  </Button>
                </div>

                <div>
                  {/*<Button 
                    title="Ändern"
                    size='small' color='inherit' variant='outlined'
                    //onClick={()=>{deleteItem(key)}}
                  >
                    <EditIcon />
                  </Button>*/}
                  <Button 
                    title="Entfernen"
                    size='small' color='inherit' variant='outlined'
                    onClick={()=>{deleteItem(key)}}
                  >
                    <ClearIcon />
                  </Button>
                </div>

              </div>

              {/* +++++++++++++++++++++++++++++++++++++++++++++++++++ */}

            </div>

          </div>

        </CardContent>

      </Card>
    })}

  </div>

}

export default WarenkorbInhalt
